import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormDrawerWrapper from '../../../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../../../features/components/Forms/EditForm/EditSectionsList'
import NameSectionPreview from '../../../ReusableFormSections/NameSection/NameSectionPreview'
import NameSection from './Sections/NameSection'
import CampaignBudgetSectionPreview from '../../../ReusableFormSections/reusableBudgetSections/FacebookBudgetSections/FacebookCampaignBudgetSection/CampaignBudgetSectionPreview'
import BudgetSection from './Sections/BudgetSection'
import SpecialAdCategoriesSectionPreview from './Sections/SpecialAdCategoriesSection/SpecialAdCategoriesSectionPreview'
import SpecialAdCategoriesSectionForm from './Sections/SpecialAdCategoriesSection/SpecialAdCategoriesSectionForm'

import { clearCampaign, getCampaign } from '../../../../modules/actions/campaigns'
import {
  campaignLoadingSelector,
  campaignSelector,
  campaignWasLoadedSelector
} from '../../../../modules/selectors/campaigns'
import { selectedFacebookAdAccountSelector } from '../../../../modules/selectors/adAccounts'

import useManageEditFormData from '../../../../hooks/formHooks/useManageEditFormData'
import usePermissions from '../../../../hooks/usePermissions'
import { insertIf } from '../../../../helpers/common'

import { CAMPAIGN_FACEBOOK_EDIT } from '../../../../constants/forms'
import { NAME, CAMPAIGN_BUDGET, SPECIAL_AD_CATEGORIES } from '../../../Multiplatform/CampaignForms/fields'
import { FACEBOOK_PLATFORM } from '../../../../constants/selectLists/platformList'
import { CAMPAIGN_BUDGET_SECTION } from '../../../../constants/permissions'

// here defined initial open states for CampaignFacebookEdit sections
const sectionsInitialOpenStates = {
  [NAME]: false,
  [CAMPAIGN_BUDGET]: false,
  [SPECIAL_AD_CATEGORIES]: false
}

const CampaignFacebookEdit = ({ shouldBeCleared }) => {
  const dispatch = useDispatch()

  const permissions = usePermissions()

  const campaign = useSelector(campaignSelector)
  const isCampaignLoading = useSelector(campaignLoadingSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)

  const isInitialDataLoading = isCampaignLoading || !campaignWasLoaded

  const getCampaignDataHandler = useCallback(
    selectedEditItemId => {
      // Load campaign data only if user is on campaigns list page
      // if user is on campaign summary page - we already have campaign data in store
      if (!campaign.id) {
        dispatch(getCampaign({ id: selectedEditItemId, account: selectedAdAccountId }, FACEBOOK_PLATFORM))
      }
    },
    [dispatch, selectedAdAccountId, campaign.id]
  )

  const clearCampaignDataHandler = useCallback(() => {
    if (shouldBeCleared) {
      dispatch(clearCampaign())
    }
  }, [dispatch, shouldBeCleared])

  useManageEditFormData({
    formName: CAMPAIGN_FACEBOOK_EDIT,
    loadedDataId: campaign.id,
    getDataHandler: getCampaignDataHandler,
    clearDataHandler: clearCampaignDataHandler
  })

  // here defined all edit sections for CampaignFacebookEdit
  const editSections = useMemo(
    () => [
      {
        sectionName: NAME,
        title: 'Name',
        PreviewComponent: <NameSectionPreview dataSelector={campaignSelector} />,
        FormComponent: <NameSection />
      },
      ...insertIf(permissions.can('update', CAMPAIGN_BUDGET_SECTION), {
        sectionName: CAMPAIGN_BUDGET,
        title: 'Budget',
        PreviewComponent: <CampaignBudgetSectionPreview campaign={campaign} />,
        // there is case for BudgetSection when edit form should not be represented
        // so instead of rendering form the description is represented
        FormComponent: <BudgetSection />
      }),
      {
        sectionName: SPECIAL_AD_CATEGORIES,
        title: 'Special Ad Categories',
        PreviewComponent: <SpecialAdCategoriesSectionPreview />,
        FormComponent: <SpecialAdCategoriesSectionForm />
      }
    ],
    [permissions, campaign]
  )

  return (
    <FormDrawerWrapper title="Edit a Facebook campaign" formName={CAMPAIGN_FACEBOOK_EDIT} showOpenButton={false}>
      <EditSectionsList
        editSections={editSections}
        isEditDataLoading={isInitialDataLoading}
        editItemData={campaign}
        sectionsInitialOpenStates={sectionsInitialOpenStates}
      />
    </FormDrawerWrapper>
  )
}

export default CampaignFacebookEdit
